import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import Modal from "../../Modal";
import Icon from "../../Icon";
import CollabBadge, { CollabTrustStats } from "../../CollabBadge";
import styles from "./ProfileModal.module.sass";

/**
 * ProfileModal - Shows the profile of the other user in a DM conversation.
 * For hosts, displays a property grid with in-modal property detail drilldown.
 * For creators, displays their public profile info.
 */
const ProfileModal = ({ visible, onClose, otherUser }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [profileType, setProfileType] = useState(null); // 'host' | 'creator'
    const [hostProfile, setHostProfile] = useState(null);
    const [creatorProfile, setCreatorProfile] = useState(null);
    const [properties, setProperties] = useState([]);
    const [hostReviews, setHostReviews] = useState([]);
    const [creatorReviews, setCreatorReviews] = useState([]);
    
    // Property drilldown state
    const [selectedPropertyId, setSelectedPropertyId] = useState(null);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [propertyReviews, setPropertyReviews] = useState([]);
    const [propertyLoading, setPropertyLoading] = useState(false);
    
    const apiBase = typeof window !== 'undefined' && window.location.hostname === 'localhost' 
        ? 'http://localhost:4000' 
        : '';
    
    // Fetch profile data when modal opens
    const fetchProfile = useCallback(async () => {
        if (!otherUser || !visible) return;
        
        setLoading(true);
        setError(null);
        setSelectedPropertyId(null);
        setSelectedProperty(null);
        
        try {
            const { userType, handle } = otherUser;
            
            if (userType === 'host') {
                // Fetch host profile
                const queryParam = handle ? `handle=${encodeURIComponent(handle)}` : '';
                if (!queryParam) {
                    setError('Unable to load profile');
                    setLoading(false);
                    return;
                }
                
                const res = await fetch(`${apiBase}/api/host/profile?${queryParam}`);
                const data = await res.json();
                
                if (res.ok && data.ok && data.profile) {
                    setProfileType('host');
                    setHostProfile(data.profile);
                    setProperties(data.properties || []);
                    setHostReviews(data.hostReviews || []);
                } else {
                    setError('Profile not found');
                }
            } else if (userType === 'creator') {
                // Fetch creator profile
                const queryParam = handle ? `handle=${encodeURIComponent(handle)}` : '';
                if (!queryParam) {
                    setError('Unable to load profile');
                    setLoading(false);
                    return;
                }
                
                const res = await fetch(`${apiBase}/api/creator/profile?${queryParam}`);
                const data = await res.json();
                
                if (res.ok && data.ok && data.profile) {
                    setProfileType('creator');
                    setCreatorProfile(data.profile);
                    setCreatorReviews(data.reviews || []);
                } else {
                    setError('Profile not found');
                }
            } else {
                setError('Unknown profile type');
            }
        } catch (err) {
            console.error('Failed to fetch profile:', err);
            setError('Failed to load profile');
        } finally {
            setLoading(false);
        }
    }, [otherUser, visible, apiBase]);
    
    useEffect(() => {
        if (visible) {
            fetchProfile();
        } else {
            // Reset state when modal closes
            setProfileType(null);
            setHostProfile(null);
            setCreatorProfile(null);
            setProperties([]);
            setSelectedPropertyId(null);
            setSelectedProperty(null);
            setError(null);
        }
    }, [visible, fetchProfile]);
    
    // Fetch property detail when a property is selected
    const fetchPropertyDetail = useCallback(async (propertyId) => {
        if (!hostProfile?.host_profile_id || !propertyId) return;
        
        setPropertyLoading(true);
        try {
            const res = await fetch(
                `${apiBase}/api/host/profile?host_id=${hostProfile.host_profile_id}&property_id=${propertyId}`
            );
            const data = await res.json();
            
            if (res.ok && data.ok && data.property) {
                setSelectedProperty(data.property);
                setPropertyReviews(data.reviews || []);
            }
        } catch (err) {
            console.error('Failed to fetch property:', err);
        } finally {
            setPropertyLoading(false);
        }
    }, [hostProfile?.host_profile_id, apiBase]);
    
    useEffect(() => {
        if (selectedPropertyId) {
            fetchPropertyDetail(selectedPropertyId);
        }
    }, [selectedPropertyId, fetchPropertyDetail]);
    
    // Parse socials helper
    const parseSocials = (socials) => {
        if (!socials) return {};
        if (typeof socials === 'string') {
            try {
                return JSON.parse(socials);
            } catch {
                return {};
            }
        }
        return socials;
    };
    
    // Format location helper
    const formatLocation = (city, state, country) => {
        const parts = [city, state, country].filter(Boolean);
        return parts.join(', ');
    };
    
    // Render loading state
    const renderLoading = () => (
        <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading profile...</p>
        </div>
    );
    
    // Render error state
    const renderError = () => (
        <div className={styles.errorState}>
            <Icon name="close" size={32} />
            <p>{error}</p>
        </div>
    );
    
    // Render creator profile
    const renderCreatorProfile = () => {
        if (!creatorProfile) return null;
        
        const socials = parseSocials(creatorProfile.socials);
        const location = formatLocation(
            creatorProfile.location_city,
            creatorProfile.location_state,
            null
        );
        
        // Parse niches
        let niches = creatorProfile.niches || [];
        if (typeof niches === 'string') {
            try {
                niches = JSON.parse(niches);
            } catch {
                niches = [];
            }
        }
        
        const hasBanner = !!creatorProfile.banner_url;
        
        return (
            <div className={styles.profileContent}>
                {/* Header/Banner - only show if banner exists */}
                {hasBanner ? (
                    <div className={styles.profileHeader}>
                        <img 
                            src={creatorProfile.banner_url} 
                            alt="" 
                            className={styles.bannerImage}
                        />
                        <div className={styles.avatarSection}>
                            <img 
                                src={creatorProfile.avatar_url || '/images/content/avatar-1.jpg'} 
                                alt={creatorProfile.display_name || 'Creator'}
                                className={styles.profileAvatar}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.avatarOnlySection}>
                        <img 
                            src={creatorProfile.avatar_url || '/images/content/avatar-1.jpg'} 
                            alt={creatorProfile.display_name || 'Creator'}
                            className={styles.profileAvatarLarge}
                        />
                    </div>
                )}
                
                {/* Profile Info */}
                <div className={cn(styles.profileInfo, { [styles.profileInfoNoBanner]: !hasBanner })}>
                    <h2 className={styles.profileName}>
                        {creatorProfile.display_name || creatorProfile.first_name || 'Creator'}
                        <CollabBadge
                            completedCollabs={creatorProfile.completed_collabs}
                            isFounder={creatorProfile.founder}
                            ratingDistribution={creatorProfile.rating_distribution}
                            variant="stamp-inline"
                        />
                    </h2>
                    {creatorProfile.handle && (
                        <p className={styles.handle}>@{creatorProfile.handle.replace(/^@/, '')}</p>
                    )}
                    {location && (
                        <p className={styles.location}>
                            <Icon name="location" size={14} />
                            {location}
                        </p>
                    )}
                    <CollabTrustStats
                        completedCollabs={creatorProfile.completed_collabs}
                        averageRating={creatorProfile.average_rating}
                        totalReviews={creatorProfile.total_reviews}
                        reviewsHref={creatorProfile.handle ? `/profile?handle=${encodeURIComponent(creatorProfile.handle.replace(/^@/, ''))}#reviews` : null}
                    />
                </div>
                
                {/* Bio */}
                {creatorProfile.bio && (
                    <div className={styles.section}>
                        <p className={styles.bio}>{creatorProfile.bio}</p>
                    </div>
                )}
                
                {/* Niches */}
                {niches.length > 0 && (
                    <div className={styles.section}>
                        <h4 className={styles.sectionTitle}>Content Categories</h4>
                        <div className={styles.tags}>
                            {niches.map((niche, i) => (
                                <span key={i} className={styles.tag}>
                                    {niche.replace(/_/g, ' ')}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Socials */}
                {Object.keys(socials).length > 0 && (
                    <div className={styles.section}>
                        <h4 className={styles.sectionTitle}>Social Links</h4>
                        <div className={styles.socialLinks}>
                            {socials.instagram && (
                                <a 
                                    href={socials.instagram.url || socials.instagram} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className={styles.socialLink}
                                >
                                    Instagram
                                </a>
                            )}
                            {socials.tiktok && (
                                <a 
                                    href={socials.tiktok.url || socials.tiktok} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className={styles.socialLink}
                                >
                                    TikTok
                                </a>
                            )}
                            {socials.youtube && (
                                <a 
                                    href={socials.youtube.url || socials.youtube} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className={styles.socialLink}
                                >
                                    YouTube
                                </a>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Media Kit */}
                {creatorProfile.media_kit_url && (
                    <div className={styles.section}>
                        <a 
                            href={creatorProfile.media_kit_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.mediaKitLink}
                        >
                            View Media Kit
                        </a>
                    </div>
                )}
                
                {/* Reviews */}
                {creatorReviews.length > 0 && (
                    <div className={styles.section}>
                        <h4 className={styles.sectionTitle}>
                            Reviews ({creatorProfile.total_reviews || creatorReviews.length})
                        </h4>
                        {creatorProfile.average_rating && (
                            <div className={styles.rating}>
                                <span className={styles.ratingValue}>
                                    {Number(creatorProfile.average_rating).toFixed(1)}
                                </span>
                                <span className={styles.stars}>★</span>
                            </div>
                        )}
                        <div className={styles.reviewsList}>
                            {creatorReviews.slice(0, 3).map((review) => (
                                <div key={review.id} className={styles.reviewCard}>
                                    <div className={styles.reviewHeader}>
                                        <span className={styles.reviewerName}>
                                            {review.host_name || 'Host'}
                                        </span>
                                        <span className={styles.reviewRating}>
                                            {'★'.repeat(review.rating)}
                                        </span>
                                    </div>
                                    <p className={styles.reviewText}>{review.review_text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };
    
    // Render host profile (with properties grid)
    const renderHostProfile = () => {
        if (!hostProfile) return null;
        
        const socials = parseSocials(hostProfile.socials);
        const hasBanner = !!hostProfile.banner_url;
        
        return (
            <div className={styles.profileContent}>
                {/* Header/Banner - only show if banner exists */}
                {hasBanner ? (
                    <div className={styles.profileHeader}>
                        <img 
                            src={hostProfile.banner_url} 
                            alt="" 
                            className={styles.bannerImage}
                        />
                        <div className={styles.avatarSection}>
                            <img 
                                src={hostProfile.logo_url || '/images/content/avatar-1.jpg'} 
                                alt={hostProfile.brand_name || 'Host'}
                                className={styles.profileAvatar}
                            />
                        </div>
                    </div>
                ) : (
                    <div className={styles.avatarOnlySection}>
                        <img 
                            src={hostProfile.logo_url || '/images/content/avatar-1.jpg'} 
                            alt={hostProfile.brand_name || 'Host'}
                            className={styles.profileAvatarLarge}
                        />
                    </div>
                )}
                
                {/* Profile Info */}
                <div className={cn(styles.profileInfo, { [styles.profileInfoNoBanner]: !hasBanner })}>
                    <h2 className={styles.profileName}>
                        {hostProfile.brand_name || hostProfile.owner_name || 'Host'}
                        <CollabBadge
                            completedCollabs={hostProfile.completed_collabs}
                            isFounder={hostProfile.founder}
                            ratingDistribution={hostProfile.rating_distribution}
                            variant="stamp-inline"
                        />
                    </h2>
                    {hostProfile.handle && (
                        <p className={styles.handle}>@{hostProfile.handle.replace(/^@/, '')}</p>
                    )}
                    <CollabTrustStats
                        completedCollabs={hostProfile.completed_collabs}
                        averageRating={hostProfile.average_rating}
                        totalReviews={hostProfile.total_reviews}
                        reviewsHref={hostProfile.handle ? `/profile?handle=${encodeURIComponent(hostProfile.handle.replace(/^@/, ''))}#reviews` : null}
                    />
                </div>
                
                {/* Description */}
                {hostProfile.description && (
                    <div className={styles.section}>
                        <p className={styles.bio}>{hostProfile.description}</p>
                    </div>
                )}
                
                {/* Links */}
                <div className={styles.section}>
                    <div className={styles.socialLinks}>
                        {hostProfile.linktree_url && (
                            <a 
                                href={hostProfile.linktree_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={styles.socialLink}
                            >
                                Website
                            </a>
                        )}
                        {socials.instagram && (
                            <a 
                                href={socials.instagram.url || socials.instagram} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={styles.socialLink}
                            >
                                Instagram
                            </a>
                        )}
                        {socials.tiktok && (
                            <a 
                                href={socials.tiktok.url || socials.tiktok} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className={styles.socialLink}
                            >
                                TikTok
                            </a>
                        )}
                    </div>
                </div>
                
                {/* Host Reviews */}
                {hostReviews.length > 0 && (
                    <div className={styles.section}>
                        <h4 className={styles.sectionTitle}>Host Reviews</h4>
                        <div className={styles.reviewsList}>
                            {hostReviews.slice(0, 3).map((review) => (
                                <div key={review.id} className={styles.reviewCard}>
                                    <div className={styles.reviewHeader}>
                                        <span className={styles.reviewerName}>
                                            {review.creator_name || 'Creator'}
                                        </span>
                                        <span className={styles.reviewRating}>
                                            {'★'.repeat(review.rating)}
                                        </span>
                                    </div>
                                    <p className={styles.reviewText}>{review.review_text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                
                {/* Properties Grid */}
                <div className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        Properties ({properties.length})
                    </h4>
                    {properties.length === 0 ? (
                        <p className={styles.emptyText}>No properties listed yet.</p>
                    ) : (
                        <div className={styles.propertiesGrid}>
                            {properties.map((prop) => (
                                <button
                                    key={prop.id}
                                    className={styles.propertyCard}
                                    onClick={() => setSelectedPropertyId(prop.id)}
                                >
                                    <div className={styles.propertyImage}>
                                        <img 
                                            src={prop.main_image_url || '/images/content/photo-1.jpg'} 
                                            alt={prop.name}
                                        />
                                    </div>
                                    <div className={styles.propertyInfo}>
                                        <h5 className={styles.propertyName}>{prop.name}</h5>
                                        <p className={styles.propertyLocation}>
                                            {formatLocation(prop.location_city, prop.location_state, prop.location_country)}
                                        </p>
                                        {(prop.bedrooms || prop.bathrooms) && (
                                            <p className={styles.propertyDetails}>
                                                {prop.bedrooms && `${prop.bedrooms} bed`}
                                                {prop.bedrooms && prop.bathrooms && ' · '}
                                                {prop.bathrooms && `${prop.bathrooms} bath`}
                                            </p>
                                        )}
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };
    
    // Render property detail view
    const renderPropertyDetail = () => {
        if (propertyLoading) {
            return (
                <div className={styles.loadingState}>
                    <div className={styles.spinner} />
                    <p>Loading property...</p>
                </div>
            );
        }
        
        if (!selectedProperty) return null;
        
        const location = formatLocation(
            selectedProperty.location_city,
            selectedProperty.location_state,
            selectedProperty.location_country
        );
        
        return (
            <div className={styles.propertyDetailContent}>
                {/* Back button */}
                <button 
                    className={styles.backButton}
                    onClick={() => {
                        setSelectedPropertyId(null);
                        setSelectedProperty(null);
                    }}
                >
                    <Icon name="arrow-left" size={16} />
                    Back to Profile
                </button>
                
                {/* Property hero */}
                <div className={styles.propertyHero}>
                    <img 
                        src={selectedProperty.main_image_url || '/images/content/photo-1.jpg'} 
                        alt={selectedProperty.name}
                    />
                </div>
                
                {/* Property info */}
                <div className={styles.propertyDetailInfo}>
                    <h2 className={styles.propertyDetailName}>{selectedProperty.name}</h2>
                    {location && (
                        <p className={styles.propertyDetailLocation}>
                            <Icon name="location" size={14} />
                            {location}
                        </p>
                    )}
                    
                    {/* Badges */}
                    <div className={styles.propertyBadges}>
                        {selectedProperty.bedrooms && (
                            <span className={styles.badge}>
                                {selectedProperty.bedrooms} {selectedProperty.bedrooms === 1 ? 'Bedroom' : 'Bedrooms'}
                            </span>
                        )}
                        {selectedProperty.bathrooms && (
                            <span className={styles.badge}>
                                {Number(selectedProperty.bathrooms)} {Number(selectedProperty.bathrooms) === 1 ? 'Bathroom' : 'Bathrooms'}
                            </span>
                        )}
                        {selectedProperty.property_type && (
                            <span className={styles.badge}>
                                {selectedProperty.property_type.replace(/_/g, ' ')}
                            </span>
                        )}
                        {selectedProperty.max_guests && (
                            <span className={styles.badge}>
                                Sleeps {selectedProperty.max_guests}
                            </span>
                        )}
                    </div>
                    
                    {/* Description */}
                    {selectedProperty.description && (
                        <div className={styles.section}>
                            <p className={styles.propertyDescription}>
                                {selectedProperty.description}
                            </p>
                        </div>
                    )}
                    
                    {/* Booking links */}
                    <div className={styles.bookingLinks}>
                        {selectedProperty.direct_booking_url && (
                            <a 
                                href={selectedProperty.direct_booking_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={cn(styles.bookingLink, styles.directLink)}
                            >
                                Book Direct
                            </a>
                        )}
                        {selectedProperty.airbnb_url && (
                            <a 
                                href={selectedProperty.airbnb_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.bookingLink}
                            >
                                Airbnb
                            </a>
                        )}
                        {selectedProperty.vrbo_url && (
                            <a 
                                href={selectedProperty.vrbo_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.bookingLink}
                            >
                                VRBO
                            </a>
                        )}
                    </div>
                    
                    {/* Property reviews */}
                    {propertyReviews.length > 0 && (
                        <div className={styles.section}>
                            <h4 className={styles.sectionTitle}>
                                Guest Reviews ({selectedProperty.total_reviews || propertyReviews.length})
                            </h4>
                            {selectedProperty.average_rating && (
                                <div className={styles.rating}>
                                    <span className={styles.ratingValue}>
                                        {Number(selectedProperty.average_rating).toFixed(1)}
                                    </span>
                                    <span className={styles.stars}>★</span>
                                </div>
                            )}
                            <div className={styles.reviewsList}>
                                {propertyReviews.slice(0, 5).map((review) => (
                                    <div key={review.id} className={styles.reviewCard}>
                                        <div className={styles.reviewHeader}>
                                            <span className={styles.reviewerName}>
                                                {review.reviewer_name || 'Guest'}
                                            </span>
                                            <span className={styles.reviewRating}>
                                                {'★'.repeat(review.rating)}
                                            </span>
                                        </div>
                                        {review.title && (
                                            <h5 className={styles.reviewTitle}>{review.title}</h5>
                                        )}
                                        <p className={styles.reviewText}>{review.review_text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };
    
    // Main render
    const renderContent = () => {
        if (loading) return renderLoading();
        if (error) return renderError();
        
        // If a property is selected, show property detail
        if (selectedPropertyId && profileType === 'host') {
            return renderPropertyDetail();
        }
        
        // Show profile based on type
        if (profileType === 'host') {
            return renderHostProfile();
        } else if (profileType === 'creator') {
            return renderCreatorProfile();
        }
        
        return null;
    };
    
    return (
        <Modal 
            visible={visible} 
            onClose={onClose} 
            outerClassName={styles.modalOuter}
        >
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h3 className={styles.modalTitle}>
                        {selectedPropertyId ? 'Property Details' : 'Profile'}
                    </h3>
                </div>
                <div className={styles.modalBody}>
                    {renderContent()}
                </div>
            </div>
        </Modal>
    );
};

export default ProfileModal;

